import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity } from 'utils/helpers/client';
import { createErrorFromResponse } from 'core/Error';
import { formatDate } from 'utils/helpers/formatters';
import { getUserInfo } from 'utils/auth';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import CreateEntity from 'components/crud/CreateEntity';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Button from 'components/Button';
import styled from 'styled-components';
import { useModal } from 'store/modal';

interface IInputs {
  date: Date | null;
  timeFrom: Date | null;
  timeTo: Date | null;
  comment?: string;
  lunchBreak?: number;
}

interface FormattedHourlyAbsence {
  datetime_from: string | null;
  datetime_to: string | null;
  lunch_break: number;
  comment?: string;
}

const CreateHourlyAbsencesForm: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const userInfo = getUserInfo();
  const methods = useForm<IInputs>({
    defaultValues: {
      date: null,
      timeFrom: null,
      timeTo: null,
      comment: '',
      lunchBreak: 0,
    },
  });
  const { showModal } = useModal();

  const handleSubmit = async (data: IInputs, create: any) => {
    showModal({
      type: 'confirm',
      heading: t('common.confirm'),
      content: t('hourlyAbsences.confirmationMessageEmployee'),
      onConfirm: async () => {
        try {
          const datetimeFrom =
            data.date && data.timeFrom
              ? new Date(data.date).setHours(data.timeFrom.getHours(), data.timeFrom.getMinutes())
              : null;

          const datetimeTo =
            data.date && data.timeTo
              ? new Date(data.date).setHours(data.timeTo.getHours(), data.timeTo.getMinutes())
              : null;

          const formattedData: FormattedHourlyAbsence = {
            datetime_from: datetimeFrom ? formatDate(new Date(datetimeFrom), 'Y-mm-dd hh:ii:ss') : null,
            datetime_to: datetimeTo ? formatDate(new Date(datetimeTo), 'Y-mm-dd hh:ii:ss') : null,
            lunch_break: data.lunchBreak || 0,
          };

          if (data.comment && data.comment.trim() !== '') {
            formattedData.comment = data.comment;
          }

          await create(formattedData);
        } catch (error) {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
        }
      },
      confirmLabel: t('hourlyAbsences.confirmEmployee'),
    });
  };

  const LunchBreakSelector: FC = () => {
    const { setValue, watch } = methods;

    const selected = watch('lunchBreak', 0);

    const options = [
      { label: t('hourlyAbsences.noLunchBreak'), value: 0 },
      { label: t('hourlyAbsences.lunchbreakHalf'), value: 0.5 },
      { label: t('hourlyAbsences.lunchbreakOne'), value: 1 },
    ];

    return (
      <div className="mb-4">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label id="lunch-break-label">{t('hourlyAbsences.lunchbreak')}</label>
        <SwitchButtons
          role="group"
          aria-labelledby="lunch-break-label"
        >
          {options.map(option => (
            <Button
              key={option.value}
              type="button"
              btnType={selected === option.value ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={selected === option.value}
              onClick={() => setValue('lunchBreak', option.value)}
            >
              {option.label}
            </Button>
          ))}
        </SwitchButtons>
      </div>
    );
  };

  const formatHours = (hours: number | undefined): string => {
    if (hours === undefined || Number.isNaN(hours)) return '0';
    return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
  };

  return (
    <StyledFormGroup>
      <Row>
        <Col className="col-sm-6">
          {userInfo.totalHourlyAbsencesRemainingHours !== undefined && (
            <HourlyAbsencesInfo>
              <InfoLabel>{t('hourlyAbsences.remainingHours')}:</InfoLabel>
              <InfoValue isNegative={userInfo.totalHourlyAbsencesRemainingHours < 0}>
                {formatHours(userInfo.totalHourlyAbsencesRemainingHours)} {t('hourlyAbsences.hoursLabel')}
              </InfoValue>
              {userInfo.totalHourlyAbsencesUncoveredHours !== undefined &&
                userInfo.totalHourlyAbsencesUncoveredHours > 0 && (
                  <InfoHint>
                    ({t('hourlyAbsences.uncoveredHours')}: {formatHours(userInfo.totalHourlyAbsencesUncoveredHours)}{' '}
                    {t('hourlyAbsences.hoursLabel')})
                  </InfoHint>
                )}
              {userInfo.totalHourlyAbsencesHoursAboutToBeCovered !== undefined &&
                userInfo.totalHourlyAbsencesHoursAboutToBeCovered > 0 && (
                  <InfoHint>
                    ({t('hourlyAbsences.hoursAboutToBeCovered')}:{' '}
                    {formatHours(userInfo.totalHourlyAbsencesHoursAboutToBeCovered)} {t('hourlyAbsences.hoursLabel')})
                  </InfoHint>
                )}
            </HourlyAbsencesInfo>
          )}
          <CreateEntity
            entity="hourly-absences"
            methods={methods}
            queryKey={buildClientEntity('hourly-absences', client)}
            onSuccess={() => {
              showModal({
                type: 'success',
                content: t('hourlyAbsences.createSuccessEmployee'),
              });
              methods.reset();
            }}
          >
            {(create: any) => (
              <Form
                title={t('hourlyAbsences.createEmployee')}
                methods={methods}
                btnLabel={t('hourlyAbsences.createEmployee')}
                primaryActionBtnType="primary"
                onSubmit={(data: IInputs) => handleSubmit(data, create)}
              >
                <FormGroup
                  controlId="formGroupDate"
                  label={t('hourlyAbsences.inputs.date')}
                  type="datepicker"
                  name="date"
                  isRequired
                  dateFormat="dd.MM.yyyy"
                  showYearDropdown
                  showMonthDropdown
                  maxDate={new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)}
                />
                <Row className="mt-1">
                  <Col md={{ span: 6, offset: 0 }}>
                    <FormGroup
                      controlId="formGroupTimeFrom"
                      label={t('hourlyAbsences.inputs.timeFrom')}
                      type="datepicker"
                      name="timeFrom"
                      showTimeSelect
                      showTimeSelectOnly
                      isRequired
                      dateFormat="HH:mm"
                      onChange={(date: Date) => methods.clearErrors()}
                    />
                  </Col>
                  <Col md={{ span: 6, offset: 0 }}>
                    <FormGroup
                      controlId="formGroupTimeTo"
                      label={t('hourlyAbsences.inputs.timeTo')}
                      type="datepicker"
                      name="timeTo"
                      showTimeSelect
                      showTimeSelectOnly
                      isRequired
                      dateFormat="HH:mm"
                      onChange={(date: Date) => methods.clearErrors()}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroup
                      controlId="formGroupComment"
                      label={t('hourlyAbsences.comment')}
                      type="text"
                      name="comment"
                      maxLength={255}
                      hint={t('hourlyAbsences.commentInfoManualEmployee')}
                    />
                  </Col>
                </Row>
                <LunchBreakSelector />
              </Form>
            )}
          </CreateEntity>
        </Col>
      </Row>
    </StyledFormGroup>
  );
};

export default CreateHourlyAbsencesForm;

const StyledFormGroup = styled.div`
  .react-datepicker__day--weekend {
    color: #d1d1d1; /* Gray color for weekends */
  }
`;

const SwitchButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const HourlyAbsencesInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 14px;
`;

const InfoValue = styled.span<{ isNegative?: boolean }>`
  font-weight: 700;
  font-size: 16px;
  color: ${props => (props.isNegative ? '#dc3545' : '#28a745')};
`;

const InfoHint = styled.span`
  font-size: 13px;
  color: #6c757d;
  font-style: italic;
`;
